import { useTranslate } from '@refinedev/core';
import { Flex, Typography, theme } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  MergeCellsOutlined,
  MinusOutlined,
  QuestionOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type {
  PublicationImportCollisionOut,
  PublicationImportProductsOut,
} from '@/api/generated/schemas';
import { ProductImportStatus } from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { OutcomeIcon } from '@/components/common/OutcomeIcon';
import { OutcomeRow } from '@/components/common/OutcomeRow';
import { OutcomeSummary } from '@/components/common/OutcomeSummary';
import {
  isMutedStatus,
  isSuccessStatus,
  linkedPublicationId,
  problemCount,
  reasonText,
  statusText,
  statusTone,
  successPercent,
} from './productImportOutcomes';

const { Text } = Typography;

interface ProductImportResultProps {
  result: PublicationImportProductsOut;
  /** Content-catalog product titles by id; falls back to `#id`. */
  titles?: Record<number, string>;
}

/** Filled means the import acted; outlined means it looked and did nothing. */
const ICONS: Record<
  ProductImportStatus,
  { glyph: React.ReactNode; variant: 'filled' | 'outlined' }
> = {
  imported: { glyph: <CheckOutlined />, variant: 'filled' },
  fused: { glyph: <MergeCellsOutlined />, variant: 'filled' },
  failed: { glyph: <CloseOutlined />, variant: 'filled' },
  blocked: { glyph: <StopOutlined />, variant: 'filled' },
  product_dropped: { glyph: <StopOutlined />, variant: 'filled' },
  publication_imported_product_missing: {
    glyph: <WarningOutlined />,
    variant: 'filled',
  },
  unknown_product: { glyph: <QuestionOutlined />, variant: 'outlined' },
  already_imported: { glyph: <MinusOutlined />, variant: 'outlined' },
  skipped: { glyph: <MinusOutlined />, variant: 'outlined' },
};

/** Summary bar plus one row per product, in the order the backend returned them. */
export const ProductImportResult = ({
  result,
  titles,
}: ProductImportResultProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const k = (key: string, opts?: Record<string, unknown>) =>
    t(`user_catalog.publications.import_products.result.${key}`, opts);

  const problems = problemCount(result);
  const created = result.imported + result.fused;

  /**
   * Contributors and series the import could not attach. The publication is
   * written either way, so these are notes on an otherwise fine row.
   */
  const collisionLines = (collisions: PublicationImportCollisionOut[]) =>
    collisions.map((c) => reasonText(t, c.code, c.context) ?? c.code);

  return (
    <Flex vertical gap={token.marginSM}>
      <OutcomeSummary
        headline={
          problems > 0
            ? k('header_problems', { count: problems })
            : k('header_ok', { count: created })
        }
        aside={k('products', { count: result.requested })}
        percent={successPercent(result)}
        exception={result.failed > 0}
        progressLabel={k('products', { count: result.requested })}
      />
      <Flex
        vertical
        gap={token.marginXS}
        style={{ maxHeight: 420, overflowY: 'auto' }}
      >
        {result.outcomes.map((o) => {
          const tone = statusTone(o.status);
          const reason = reasonText(t, o.reason_code, o.reason_context);
          const publicationId = linkedPublicationId(o);
          // An unknown status has no icon; the outlined dash reads as
          // "nothing recognised happened", which is exactly right.
          const badge = ICONS[o.status] ?? {
            glyph: <MinusOutlined />,
            variant: 'outlined' as const,
          };
          return (
            <OutcomeRow
              key={o.product_id}
              tone={tone}
              muted={isMutedStatus(o.status)}
              icon={
                <OutcomeIcon tone={tone} variant={badge.variant}>
                  {badge.glyph}
                </OutcomeIcon>
              }
              title={
                <Flex gap={token.marginXS} align="baseline" wrap>
                  <Text strong>
                    {titles?.[o.product_id] ?? `#${o.product_id}`}
                  </Text>
                  <Text type="secondary">#{o.product_id}</Text>
                </Flex>
              }
            >
              <Text
                type={
                  o.status === ProductImportStatus.failed
                    ? 'danger'
                    : 'secondary'
                }
                style={{
                  display: 'block',
                  color: isSuccessStatus(o.status)
                    ? token.colorSuccess
                    : undefined,
                }}
              >
                {statusText(t, o.status)}
              </Text>
              {reason && (
                <Text type="secondary" style={{ display: 'block' }}>
                  {reason}
                </Text>
              )}
              {publicationId != null && (
                <Text type="secondary" style={{ display: 'block' }}>
                  {o.fused_into != null ? k('fused_into') : k('publication')}:{' '}
                  <ShowLink
                    resource="user_catalog.publications"
                    id={publicationId}
                  >
                    #{publicationId}
                  </ShowLink>
                </Text>
              )}
              {collisionLines(o.collisions ?? []).map((line, i) => (
                <Text key={i} type="warning" style={{ display: 'block' }}>
                  {k('collision')}: {line}
                </Text>
              ))}
            </OutcomeRow>
          );
        })}
      </Flex>
    </Flex>
  );
};
